/**
 * Host-detection config for the organizer ("creator") tree.
 *
 * Production splits the SPA by hostname: the main domain serves the public
 * event-discovery app, while a dedicated organizer subdomain (VITE_ORGANIZER_HOST)
 * serves the creator/proloco console. Locally the dev server only ever serves
 * one host on port 3011, so VITE_FORCE_ORGANIZER_HOST is the escape hatch that
 * lets a developer exercise the organizer tree without a second hostname.
 */

/**
 * True when the current page is on the configured organizer host, or when
 * the local-dev force flag is set. Never throws when the env vars are
 * missing — a missing VITE_ORGANIZER_HOST simply means "not the organizer
 * host".
 */
export function isOrganizerHost(): boolean {
  if (import.meta.env.VITE_FORCE_ORGANIZER_HOST === 'true') {
    return true;
  }

  const organizerHost = import.meta.env.VITE_ORGANIZER_HOST;
  if (!organizerHost) {
    return false;
  }

  return window.location.hostname === organizerHost;
}

/** Cookie domain shared across the main app and the organizer subdomain. */
export const COOKIE_DOMAIN = import.meta.env.VITE_COOKIE_DOMAIN;

/**
 * Origin of the public event site. Event cards link to `/event/{id}`
 * relatively on hosts that serve that route; the organizer console does not,
 * so there the same links (and the dashboard's "open the public event" action)
 * must use this absolute URL instead of a dangling organizzatori… path.
 */
export const PUBLIC_SITE_ORIGIN = 'https://buzzeventmap.com';

/**
 * True for localhost, 127.0.0.1, and the IPv6 loopback address. Browsers
 * report the IPv6 loopback hostname without brackets ("::1"), but both forms
 * are checked here in case a caller stubs `location.hostname` with brackets.
 */
export function isLocalhost(): boolean {
  const hostname = window.location.hostname;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname === '[::1]'
  );
}

/** Production analytics stay enabled; local pages and preview tunnels cannot submit events. */
export function shouldTrackAnalytics(): boolean {
  return !isLocalhost() && !window.location.hostname.toLowerCase().endsWith('.trycloudflare.com');
}

/**
 * True when the current hostname is COOKIE_DOMAIN itself, or an actual
 * subdomain of it — i.e. when a `domain=<COOKIE_DOMAIN>` cookie set here
 * would actually be accepted by the browser. False for localhost, for
 * Vercel preview hosts (`*.vercel.app`), and for any other host that isn't
 * part of the configured cookie domain, or when COOKIE_DOMAIN itself is
 * unset.
 *
 * This subsumes `isLocalhost()` as a special case and is the general rule:
 * a browser silently drops a Set-Cookie whose Domain attribute isn't the
 * current host or a parent of it, with no error surfaced anywhere, so this
 * has to be a code-level check rather than something env-var scoping can
 * be relied on to get right in every environment.
 */
export function canUseCookieDomain(): boolean {
  // Reads import.meta.env directly (like isOrganizerHost above) rather
  // than the frozen COOKIE_DOMAIN const, which is bound once at module
  // load and won't reflect env changes made after that — irrelevant at
  // runtime (env vars are build-time constants) but needed for this
  // function to be testable with vi.stubEnv.
  const cookieDomain = import.meta.env.VITE_COOKIE_DOMAIN;
  if (!cookieDomain) return false;

  const hostname = window.location.hostname;
  const bareDomain = cookieDomain.replace(/^\./, '');
  return hostname === bareDomain || hostname.endsWith(`.${bareDomain}`);
}
